import React, { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageContainer, PageHeader, PrimaryButton, SecondaryButton, EmptyState, Field, inputCls } from "@/components/ui-kit";
import { toast } from "sonner";
import { Plus, X, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, ExternalLink, UploadCloud, Link2 } from "lucide-react";

function AddImageModal({ onClose, onCreated }) {
  const [mode, setMode] = useState("upload"); // "upload" | "url"
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  // Cleanup local object URLs to avoid memory leaks
  useEffect(() => {
    return () => { if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const pickFile = (f) => {
    if (!f) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(f.type)) {
      toast.error(`Formato no soportado (${f.type || "desconocido"})`);
      return;
    }
    const maxMB = 10;
    if (f.size > maxMB * 1024 * 1024) {
      toast.error(`Imagen muy pesada (${(f.size / 1024 / 1024).toFixed(1)}MB). Máx ${maxMB}MB`);
      return;
    }
    setFile(f);
    if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) pickFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mode === "upload") {
        if (!file) { toast.error("Selecciona una imagen"); setSaving(false); return; }
        const form = new FormData();
        form.append("file", file);
        form.append("caption", caption.trim());
        await api.post("/site/gallery/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/site/gallery", { url: url.trim(), caption: caption.trim() });
      }
      toast.success("Imagen agregada");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const urlLooksValid = /^https?:\/\/\S+/i.test(url.trim());
  const canSubmit = mode === "upload" ? !!file : urlLooksValid;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-xl border-t-4 border-[#FF5A00] max-h-[95vh] overflow-y-auto" data-testid="gallery-add-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Nueva imagen</div>
            <div className="font-display font-bold text-xl">Agregar al carrusel</div>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Mode tabs */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setMode("upload")}
              data-testid="gallery-mode-upload"
              className={`flex-1 px-3 py-2 font-mono-kpi text-[10px] uppercase tracking-[0.18em] border ${mode === "upload" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-neutral-500 border-neutral-300 hover:border-[#FF5A00]"}`}
            >
              <UploadCloud className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Subir archivo
            </button>
            <button
              type="button"
              onClick={() => setMode("url")}
              data-testid="gallery-mode-url"
              className={`flex-1 px-3 py-2 font-mono-kpi text-[10px] uppercase tracking-[0.18em] border ${mode === "url" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-neutral-500 border-neutral-300 hover:border-[#FF5A00]"}`}
            >
              <Link2 className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Desde URL
            </button>
          </div>

          {mode === "upload" ? (
            <div>
              <div
                data-testid="gallery-dropzone"
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragging ? "border-[#FF5A00] bg-[#FF5A00]/5" : "border-neutral-300 bg-neutral-50 hover:border-[#FF5A00] hover:bg-[#FF5A00]/5"}`}
              >
                <UploadCloud className={`w-10 h-10 mx-auto mb-3 ${dragging ? "text-[#FF5A00]" : "text-neutral-400"}`} />
                <div className="font-mono-kpi text-xs font-bold uppercase tracking-[0.18em] text-neutral-800">
                  {file ? file.name : "Arrastra la imagen aquí"}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  o haz click para elegir · JPG, PNG, WebP, GIF · máx 10MB
                </div>
                {file && (
                  <div className="mt-2 text-[11px] font-mono-kpi text-neutral-400">
                    {(file.size / 1024).toFixed(0)} KB · {file.type}
                  </div>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  data-testid="gallery-file-input"
                  onChange={(e) => pickFile(e.target.files?.[0])}
                />
              </div>
              {file && (
                <button
                  type="button"
                  onClick={() => { setFile(null); if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl); setPreviewUrl(""); }}
                  className="mt-2 text-xs font-mono-kpi uppercase tracking-wider text-neutral-500 hover:text-[#D50000]"
                >
                  <X className="w-3 h-3 inline mr-1" /> Quitar archivo
                </button>
              )}
            </div>
          ) : (
            <Field label="URL de la imagen *">
              <input required type="url" data-testid="gallery-url-input"
                placeholder="https://…"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setPreviewUrl(e.target.value); }}
                className={inputCls} />
            </Field>
          )}

          <Field label="Descripción (opcional)">
            <input placeholder="Ej: Mantención hotel Cumbres" value={caption} onChange={(e) => setCaption(e.target.value)} className={inputCls} />
          </Field>

          {previewUrl && (
            <div>
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-2">Vista previa</div>
              <div className="aspect-video bg-neutral-100 border border-neutral-200 overflow-hidden">
                <img src={previewUrl} alt="Vista previa" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving || !canSubmit} data-testid="gallery-save-btn">
            {saving ? (mode === "upload" ? "Subiendo…" : "Guardando…") : "Agregar imagen"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}

export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // {id, caption}

  const load = () => api.get("/site/gallery").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta imagen del carrusel?")) return;
    try {
      await api.delete(`/site/gallery/${id}`);
      toast.success("Imagen eliminada");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setItems(reordered);
    try {
      await api.post("/site/gallery/reorder", { ids: reordered.map((r) => r.id) });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      load();
    }
  };

  const saveCaption = async () => {
    if (!editing) return;
    try {
      await api.patch(`/site/gallery/${editing.id}`, { caption: editing.caption });
      toast.success("Descripción actualizada");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        kicker={`Carrusel de la landing — ${items.length} imagen(es)`}
        title="Galería pública"
        subtitle="Estas imágenes rotan en la sección /trabajos de la página pública. Cambiá el orden con las flechas o eliminá para reemplazar."
        actions={
          <PrimaryButton data-testid="gallery-add-btn" onClick={() => setShowAdd(true)}>
            <Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" /> Agregar imagen
          </PrimaryButton>
        }
      />

      {loading ? (
        <div className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-[0.2em]">Cargando…</div>
      ) : items.length === 0 ? (
        <EmptyState title="Sin imágenes" description="El carrusel se ocultará hasta que agregues al menos una imagen." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <div key={it.id} data-testid={`gallery-item-${it.id}`} className="bg-white border border-neutral-200 group">
              <div className="relative aspect-video bg-neutral-100 overflow-hidden">
                <img src={it.url} alt={it.caption || `Imagen ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                <div className="absolute top-2 left-2 font-mono-kpi text-[10px] font-bold uppercase tracking-[0.2em] bg-[#0A0A0A] text-white px-2 py-1">
                  #{String(i + 1).padStart(2, "0")}
                </div>
              </div>
              <div className="p-3 space-y-2">
                {editing?.id === it.id ? (
                  <div className="space-y-2">
                    <input value={editing.caption} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} className={inputCls} placeholder="Descripción" />
                    <div className="flex items-center justify-end gap-2">
                      <SecondaryButton onClick={() => setEditing(null)}>Cancelar</SecondaryButton>
                      <PrimaryButton onClick={saveCaption} data-testid={`gallery-caption-save-${it.id}`}>Guardar</PrimaryButton>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setEditing({ id: it.id, caption: it.caption || "" })} className="w-full text-left text-sm text-neutral-600 hover:text-[#FF5A00] truncate min-h-[24px]" data-testid={`gallery-caption-${it.id}`}>
                    {it.caption || <span className="text-neutral-300 italic">Agregar descripción…</span>}
                  </button>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
                  <div className="flex items-center gap-1">
                    <button data-testid={`gallery-up-${it.id}`} onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed border border-neutral-200" title="Subir">
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`gallery-down-${it.id}`} onClick={() => move(i, +1)} disabled={i === items.length - 1} className="p-1.5 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed border border-neutral-200" title="Bajar">
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={it.url} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-neutral-100 border border-neutral-200 text-neutral-600" title="Abrir imagen">
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <button data-testid={`gallery-delete-${it.id}`} onClick={() => remove(it.id)} className="p-1.5 hover:bg-[#D50000]/10 border border-neutral-200 text-neutral-600 hover:text-[#D50000] hover:border-[#D50000]/40" title="Eliminar">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 text-xs text-neutral-500">
        <ImageIcon className="w-3.5 h-3.5" />
        Los cambios se reflejan en <b>allactive.cl</b> inmediatamente al recargar la landing pública.
      </div>

      {showAdd && <AddImageModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
    </PageContainer>
  );
}
